import React from 'react';
import { ArchivalImageCard } from './ArchivalImageCard';
import { BookOpen, Feather, Heart, Compass, Users, Hammer, Book, ArrowRight } from 'lucide-react';
import { ArchiveTab } from '../types';

interface HomepageProps {
  onNavigate?: (tab: ArchiveTab) => void;
}

export const Homepage: React.FC<HomepageProps> = ({ onNavigate }) => {
  return (
    <div className="max-w-4xl mx-auto space-y-12 pb-16 font-sans text-[#2c221e]">
      
      {/* Banner / Header Title Block */}
      <header className="bg-[#f3ede0] p-5 sm:p-8 md:p-12 rounded-2xl sm:rounded-3xl border-2 border-[#d8ceb9] shadow-xs relative overflow-hidden space-y-3.5 sm:space-y-4">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#e8dec9]/50 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />
        
        <div className="inline-flex items-center gap-2 px-3 sm:px-3.5 py-1.5 rounded-full bg-[#e8dec9] text-[#7a5b3a] text-[11px] sm:text-xs font-serif font-bold tracking-wider uppercase border border-[#d8ceb9]">
          <Feather className="w-3.5 h-3.5 text-[#7a5b3a] shrink-0" />
          <span>Christine Negus Archival Compilation</span>
        </div>

        <h1 className="font-serif text-2xl sm:text-4xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight leading-tight">
          Stories from the First Family Members in New Zealand
        </h1>

        <p className="font-serif text-sm sm:text-lg md:text-xl text-[#7a5b3a] italic leading-relaxed border-l-4 border-[#7a5b3a] pl-3 sm:pl-4 my-2">
          A digital preservation of Canterbury pioneers, family letters, and ancestral memories from December 1850 to 2021.
        </p>
      </header>

      {/* Quick Navigation Cards */}
      {onNavigate && (
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b-2 border-[#d8ceb9] pb-2">
            <h2 className="font-serif text-lg sm:text-xl md:text-2xl font-bold text-[#2c221e]">
              Explore the Family Archives
            </h2>
            <span className="text-xs font-serif text-[#7a5b3a] italic">
              Digital Chapters
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-4">
            {/* The Journey */}
            <div
              onClick={() => onNavigate('journey')}
              className="p-4 sm:p-5 rounded-2xl bg-[#fbf9f4] border-2 border-[#d8ceb9] hover:border-[#7a5b3a] shadow-xs hover:shadow-md cursor-pointer transition flex flex-col justify-between space-y-3 group min-h-[140px]"
            >
              <div className="space-y-2">
                <div className="w-10 h-10 rounded-xl bg-[#f3ede0] border border-[#d8ceb9] flex items-center justify-center text-[#7a5b3a] group-hover:bg-[#e8dec9] transition shrink-0">
                  <Compass className="w-5 h-5" />
                </div>
                <h3 className="font-serif font-bold text-base sm:text-lg text-[#2c221e] group-hover:text-[#7a5b3a] transition">
                  The Journey
                </h3>
                <p className="text-xs text-[#4a3525] leading-relaxed">
                  The 1850 passage, Suffolk roots, outfit packing lists, and early Canterbury settlement.
                </p>
              </div>
              <div className="flex items-center gap-1 text-xs font-serif font-bold text-[#7a5b3a] pt-1">
                <span>View 3 Sub-chapters</span>
                <ArrowRight className="w-3.5 h-3.5 transition group-hover:translate-x-1" />
              </div>
            </div>

            {/* The Dentons */}
            <div
              onClick={() => onNavigate('dentons')}
              className="p-4 sm:p-5 rounded-2xl bg-[#fbf9f4] border-2 border-[#d8ceb9] hover:border-[#7a5b3a] shadow-xs hover:shadow-md cursor-pointer transition flex flex-col justify-between space-y-3 group min-h-[140px]"
            >
              <div className="space-y-2">
                <div className="w-10 h-10 rounded-xl bg-[#f3ede0] border border-[#d8ceb9] flex items-center justify-center text-[#7a5b3a] group-hover:bg-[#e8dec9] transition shrink-0">
                  <Feather className="w-5 h-5" />
                </div>
                <h3 className="font-serif font-bold text-base sm:text-lg text-[#2c221e] group-hover:text-[#7a5b3a] transition">
                  The Dentons
                </h3>
                <p className="text-xs text-[#4a3525] leading-relaxed">
                  Tommy at Gallipoli, WW1 letters, Captain Harry Denton DFC, and Land Girl Isabel Denton.
                </p>
              </div>
              <div className="flex items-center gap-1 text-xs font-serif font-bold text-[#7a5b3a] pt-1">
                <span>Explore Records</span>
                <ArrowRight className="w-3.5 h-3.5 transition group-hover:translate-x-1" />
              </div>
            </div>

            {/* The Gibb Family */}
            <div
              onClick={() => onNavigate('gibbs')}
              className="p-4 sm:p-5 rounded-2xl bg-[#fbf9f4] border-2 border-[#d8ceb9] hover:border-[#7a5b3a] shadow-xs hover:shadow-md cursor-pointer transition flex flex-col justify-between space-y-3 group min-h-[140px]"
            >
              <div className="space-y-2">
                <div className="w-10 h-10 rounded-xl bg-[#f3ede0] border border-[#d8ceb9] flex items-center justify-center text-[#7a5b3a] group-hover:bg-[#e8dec9] transition shrink-0">
                  <Users className="w-5 h-5" />
                </div>
                <h3 className="font-serif font-bold text-base sm:text-lg text-[#2c221e] group-hover:text-[#7a5b3a] transition">
                  The Gibb Family
                </h3>
                <p className="text-xs text-[#4a3525] leading-relaxed">
                  Scottish pioneer lineage, farming enterprises, and community leadership in Canterbury.
                </p>
              </div>
              <div className="flex items-center gap-1 text-xs font-serif font-bold text-[#7a5b3a] pt-1">
                <span>Explore Records</span>
                <ArrowRight className="w-3.5 h-3.5 transition group-hover:translate-x-1" />
              </div>
            </div>

            {/* The Giles Family */}
            <div
              onClick={() => onNavigate('giles')}
              className="p-4 sm:p-5 rounded-2xl bg-[#fbf9f4] border-2 border-[#d8ceb9] hover:border-[#7a5b3a] shadow-xs hover:shadow-md cursor-pointer transition flex flex-col justify-between space-y-3 group min-h-[140px]"
            >
              <div className="space-y-2">
                <div className="w-10 h-10 rounded-xl bg-[#f3ede0] border border-[#d8ceb9] flex items-center justify-center text-[#7a5b3a] group-hover:bg-[#e8dec9] transition shrink-0">
                  <Hammer className="w-5 h-5" />
                </div>
                <h3 className="font-serif font-bold text-base sm:text-lg text-[#2c221e] group-hover:text-[#7a5b3a] transition">
                  The Giles Family
                </h3>
                <p className="text-xs text-[#4a3525] leading-relaxed">
                  Tradesmen, craftsmanship, and independence across early Canterbury townships.
                </p>
              </div>
              <div className="flex items-center gap-1 text-xs font-serif font-bold text-[#7a5b3a] pt-1">
                <span>Explore Records</span>
                <ArrowRight className="w-3.5 h-3.5 transition group-hover:translate-x-1" />
              </div>
            </div>

            {/* The Roberts Family */}
            <div
              onClick={() => onNavigate('roberts')}
              className="p-4 sm:p-5 rounded-2xl bg-[#fbf9f4] border-2 border-[#d8ceb9] hover:border-[#7a5b3a] shadow-xs hover:shadow-md cursor-pointer transition flex flex-col justify-between space-y-3 group min-h-[140px]"
            >
              <div className="space-y-2">
                <div className="w-10 h-10 rounded-xl bg-[#f3ede0] border border-[#d8ceb9] flex items-center justify-center text-[#7a5b3a] group-hover:bg-[#e8dec9] transition shrink-0">
                  <Book className="w-5 h-5" />
                </div>
                <h3 className="font-serif font-bold text-base sm:text-lg text-[#2c221e] group-hover:text-[#7a5b3a] transition">
                  The Roberts Family
                </h3>
                <p className="text-xs text-[#4a3525] leading-relaxed">
                  The literary chroniclers T.E.L. Roberts and Edward Roberts whose books inspired this history.
                </p>
              </div>
              <div className="flex items-center gap-1 text-xs font-serif font-bold text-[#7a5b3a] pt-1">
                <span>Explore Records</span>
                <ArrowRight className="w-3.5 h-3.5 transition group-hover:translate-x-1" />
              </div>
            </div>

            {/* Acknowledgement and Thanks */}
            <div
              onClick={() => onNavigate('acknowledgement')}
              className="p-4 sm:p-5 rounded-2xl bg-[#fbf9f4] border-2 border-[#d8ceb9] hover:border-[#7a5b3a] shadow-xs hover:shadow-md cursor-pointer transition flex flex-col justify-between space-y-3 group min-h-[140px]"
            >
              <div className="space-y-2">
                <div className="w-10 h-10 rounded-xl bg-[#f3ede0] border border-[#d8ceb9] flex items-center justify-center text-[#7a5b3a] group-hover:bg-[#e8dec9] transition shrink-0">
                  <Heart className="w-5 h-5" />
                </div>
                <h3 className="font-serif font-bold text-base sm:text-lg text-[#2c221e] group-hover:text-[#7a5b3a] transition">
                  Acknowledgement &amp; Thanks
                </h3>
                <p className="text-xs text-[#4a3525] leading-relaxed">
                  Dedication and gratitude to contributors, photographers, and parish registries.
                </p>
              </div>
              <div className="flex items-center gap-1 text-xs font-serif font-bold text-[#7a5b3a] pt-1">
                <span>View Dedication</span>
                <ArrowRight className="w-3.5 h-3.5 transition group-hover:translate-x-1" />
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Main Archival Document Body */}
      <article className="bg-[#fbf9f4] p-4 sm:p-8 md:p-12 lg:p-14 rounded-2xl sm:rounded-3xl border border-[#d8ceb9] shadow-xs space-y-8 sm:space-y-10">
        
        {/* ==========================================
            AUTHOR WORDS — CHRISTINE NEGUS
            ========================================== */}
        <section className="space-y-6">
          <div className="border-b-2 border-[#d8ceb9] pb-3 flex items-center justify-between">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e] flex items-center gap-2.5">
              <span className="w-3 h-3 rounded-full bg-[#7a5b3a]" />
              <span>Author Words — Christine Negus</span>
            </h2>
            <span className="text-xs font-serif text-[#7a5b3a] italic hidden sm:inline">
              Compilation Memoir
            </span>
          </div>

          {/* AUTHOR IMAGE: CHRISTINE NEGUS */}
          <ArchivalImageCard
            src="/images/1 Christine Negus.jpg"
            alt="Christine Negus, Compiler and Author"
            caption="Christine Negus, Compiler and Author"
            filenameHint="1 Christine Negus.jpg"
          />

          <p className="text-base md:text-lg leading-relaxed text-[#2c221e]">
            Christine Negus lived her first years of life on “Oakdale” with her parents and grandparents, Tom and Annie Denton (née Gibb). Forty years later, Annie spent some of her final years living with Christine in South Canterbury.
          </p>

          <p className="text-base md:text-lg leading-relaxed text-[#2c221e]">
            The people of these stories were talked about, creating a direct link with many of them — an interest that lay dormant for decades. The names came alive again as Christine read the books written by T.E.L. Roberts and Edward Roberts.
          </p>

          {/* IMAGE: ANNIE DENTON WITH SUE AND NELL ELLIOTT */}
          <ArchivalImageCard
            src="/images/2 Annie Denton with Sue and Nell Elliott, 1980.jpg"
            alt="Annie Denton with Sue and Nell Elliott, 1980"
            caption="Annie Denton with Sue and Nell Elliott, 1980"
            filenameHint="2 Annie Denton with Sue and Nell Elliott, 1980.jpg"
          />

          <p className="text-base md:text-lg leading-relaxed text-[#2c221e]">
            During the enforced confinement of Covid‑19 (2021), that interest was awakened. What began as a few family stories written for pleasure soon grew in scope — becoming an ambitious compilation to capture much of the written past and present it anew for future generations.
          </p>

          <div className="bg-[#f3ede0] p-5 md:p-6 rounded-2xl border border-[#d8ceb9] text-sm md:text-base text-[#2c221e]">
            <p className="leading-relaxed">
              <strong className="font-bold text-[#4a3525]">Background:</strong><br />
              Christine’s career includes teaching, print, radio and television journalism, freelance writing, PR and media services, and ventures into several other sectors.
            </p>
          </div>
        </section>

        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />

        {/* ==========================================
            INTRODUCTION
            ========================================== */}
        <section className="space-y-6">
          <div className="border-b-2 border-[#d8ceb9] pb-3">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
              Introduction
            </h2>
          </div>

          <p className="text-base md:text-lg leading-relaxed text-[#2c221e]">
            The movement of people around the globe created cultures, ancient and new. Just as the peoples of Polynesia came to this land, so too did our ancestors — from England, Scotland, Wales, and Ireland.
          </p>

          <p className="text-base md:text-lg leading-relaxed text-[#2c221e]">
            Their stories are for us to remember, reflect on, and enjoy. Christine presents them simply, without distortion by ideology, to keep old tales alive and give glimpses of those who lived generations ago — acknowledging their efforts to succeed.
          </p>

          {/* Subheading: Origins of the Project */}
          <div className="pt-4 space-y-4">
            <h3 className="font-serif text-xl md:text-2xl font-bold text-[#4a3525]">
              Origins of the Project
            </h3>

            <p className="text-base md:text-lg leading-relaxed text-[#2c221e]">
              This began as an effort to locate and photograph family headstones in Canterbury, recording locations and dates. That opened a vault of family stories — written, hinted, or found in official records — inspiring Christine to gather them into one booklet before they were lost.
            </p>

            {/* BLOCKQUOTE 1 */}
            <blockquote className="my-6 bg-[#f3ede0] p-6 md:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-[#4a3525] font-serif italic text-lg md:text-xl leading-relaxed shadow-xs">
              “These stories do not define lives; they are just a glimpse, a brushing‑past of an acquaintance we did not meet — but in their letters, we hear them speak directly to us.”
            </blockquote>

            {/* IMAGE: WAIKARI CEMETERY */}
            <ArchivalImageCard
              src="/images/3 Waikari cemetery.jpg"
              alt="Waikari Cemetery"
              caption="Waikari Cemetery, Canterbury"
              filenameHint="3 Waikari cemetery.jpg"
            />

            <p className="text-base md:text-lg leading-relaxed text-[#2c221e]">
              Many family members contributed ideas, stories, photographs, editing, and support. There is no single theme apart from everyday life as people lived it in their own times and places. Christine hopes readers will feel a connection with an ancestor.
            </p>

            <p className="text-base md:text-lg leading-relaxed text-[#2c221e]">
              All errors, she notes, are hers alone despite best intentions for accuracy.
            </p>
          </div>

          {/* Subheading: Later Generations */}
          <div className="pt-4 space-y-3 bg-[#e8dec9]/40 p-6 rounded-2xl border border-[#d8ceb9]">
            <h3 className="font-serif text-xl md:text-2xl font-bold text-[#4a3525]">
              Later Generations
            </h3>

            <p className="text-base md:text-lg leading-relaxed text-[#2c221e]">
              Focusing on the descendants of Tommy and Annie Denton, whose mother was a Roberts and father a Gibb, spanning the period from 1946 through 2021.
            </p>

            <p className="font-serif italic text-sm md:text-base text-[#7a5b3a] pt-2">
              Christine Negus — December 2021
            </p>
          </div>
        </section>

        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />

        {/* ==========================================
            STORIES FROM THE FIRST FAMILY MEMBERS IN NEW ZEALAND
            ========================================== */}
        <section className="space-y-6">
          <div className="border-b-2 border-[#d8ceb9] pb-3">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
              Stories from the First Family Members in New Zealand (from December 1850)
            </h2>
          </div>

          <p className="text-base md:text-lg leading-relaxed text-[#2c221e]">
            These were the pioneers — colonists and assisted immigrants who came to Canterbury.
          </p>

          <p className="text-base md:text-lg leading-relaxed text-[#2c221e]">
            Between 1800 and 1851, Britain’s population doubled, and opportunities arose in Canada, USA, Australia, and New Zealand.
          </p>

          <p className="text-base md:text-lg leading-relaxed text-[#2c221e]">
            They were tradesmen seeking security, maids seeking independence, speculators, opportunists, farmers, and importers — some here to stay, others to move on.
          </p>

          {/* IMAGE: EMIGRATION POSTER */}
          <ArchivalImageCard
            src="/images/4 Emigration poster.png"
            alt="New Zealand Emigration Poster"
            caption="New Zealand Emigration Advertisement Poster — Canterbury Settlement"
            filenameHint="4 Emigration poster.png"
          />

          {/* BLOCKQUOTE 2 */}
          <blockquote className="my-6 bg-[#f3ede0] p-6 md:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-[#4a3525] font-serif text-lg md:text-xl leading-relaxed space-y-3 shadow-xs">
            <p className="italic">
              “Very near to Australia there is a country where all the testimony concurs in describing as the fittest in the world for colonisation — the most beautiful country with the finest climate and the most productive soil; I mean New Zealand.”
            </p>
            <footer className="text-sm md:text-base font-sans not-italic font-bold text-[#7a5b3a] pt-2 border-t border-[#d8ceb9]">
              — <strong>Edward Gibbon Wakefield</strong>, House of Commons, London (1836)
            </footer>
          </blockquote>
        </section>

      </article>

      {/* Footer / Archival Note */}
      <div className="bg-[#f3ede0] p-6 rounded-2xl border border-[#d8ceb9] text-center text-xs text-[#7a5b3a] space-y-1">
        <p className="font-serif font-bold text-sm text-[#2c221e]">
          Digital Compilation: Stories from the First Family Members in New Zealand
        </p>
        <p>Compiled by Christine Negus (December 2021) • Digitized for online exploration</p>
      </div>

    </div>
  );
};
